import React, { useEffect, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/api';
import { ReportIcon } from '../lib/reportIcon';

const formatFilterLabel = (report, category) => {
  if (category === 'Research' || category === 'Equity Intelligence') {
    return [
      report.primaryFilter ? `Sector: ${report.primaryFilter}` : null,
      report.secondaryFilter ? `Report Type: ${report.secondaryFilter}` : null,
    ].filter(Boolean);
  }

  if (category === 'Legal') {
    return [
      report.primaryFilter ? `Legal Area: ${report.primaryFilter}` : null,
      report.secondaryFilter ? `Intelligence Type: ${report.secondaryFilter}` : null,
    ].filter(Boolean);
  }

  if (category === 'Learning') {
    return [
      report.primaryFilter ? `Topic: ${report.primaryFilter}` : null,
      report.secondaryFilter ? `Level: ${report.secondaryFilter}` : null,
    ].filter(Boolean);
  }

  if (category === 'Sector') {
    return [report.primaryFilter ? `Sector: ${report.primaryFilter}` : null].filter(Boolean);
  }

  return [
    report.primaryFilter ? `Content Type: ${report.primaryFilter}` : null,
    report.secondaryFilter ? `Paper: ${report.secondaryFilter}` : null,
  ].filter(Boolean);
};

const normalizeFilterValue = (value) => (value || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '');

const filterReports = (reports, filters = {}) => {
  const {
    query = '',
    primary = '',
    secondary = '',
    primaryAllValues = [],
    secondaryAllValues = [],
  } = filters;

  const normalizedQuery = query.trim().toLowerCase();
  const normalizedPrimary = normalizeFilterValue(primary);
  const normalizedSecondary = normalizeFilterValue(secondary);
  const normalizedPrimaryAll = primaryAllValues.map(normalizeFilterValue);
  const normalizedSecondaryAll = secondaryAllValues.map(normalizeFilterValue);

  return reports.filter((report) => {
    const reportPrimary = normalizeFilterValue(report.primaryFilter);
    const reportSecondary = normalizeFilterValue(report.secondaryFilter);
    const primaryMatches = !normalizedPrimary
      || normalizedPrimaryAll.includes(normalizedPrimary)
      || normalizedPrimaryAll.includes(reportPrimary)
      || reportPrimary === normalizedPrimary;
    const secondaryMatches = !normalizedSecondary
      || normalizedSecondaryAll.includes(normalizedSecondary)
      || normalizedSecondaryAll.includes(reportSecondary)
      || reportSecondary === normalizedSecondary;
    const queryMatches = !normalizedQuery
      || [report.title, report.code, report.description, report.category, report.primaryFilter, report.secondaryFilter]
        .filter(Boolean)
        .join(' ')
        .toLowerCase()
        .includes(normalizedQuery);

    return primaryMatches && secondaryMatches && queryMatches;
  });
};

const SubscriptionReportSection = ({ category, title = 'Reports', filters, onCountChange, viewMode = 'grid' }) => {
  const navigate = useNavigate();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const visibleReports = filterReports(reports, filters);

  useEffect(() => {
    onCountChange?.(visibleReports.length);
  }, [onCountChange, visibleReports.length]);

  useEffect(() => {
    let cancelled = false;

    const fetchReports = async () => {
      try {
        setLoading(true);
        setError('');
        const API = getApiUrl();
        const token = localStorage.getItem('access_token');
        const headers = {};
        if (token) {
          headers.Authorization = `Bearer ${token}`;
        }

        const resp = await fetch(`${API}/subscription-reports?category=${encodeURIComponent(category)}`, {
          method: 'GET',
          headers,
          credentials: 'include',
        });

        if (cancelled) {
          return;
        }

        if (resp.status === 401 || resp.status === 403) {
          setReports([]);
          return;
        }

        if (!resp.ok) {
          setError(`Failed to load uploaded reports (Status: ${resp.status})`);
          setReports([]);
          return;
        }

        const data = await resp.json();
        setReports(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) {
          console.error('Failed to fetch subscription reports:', e);
          setError('Failed to load uploaded reports');
          setReports([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchReports();

    return () => {
      cancelled = true;
    };
  }, [category]);

  if (loading) {
    return (
      <div className="mb-10 flex items-center justify-center py-8">
        <div className="w-8 h-8 border-2 border-[#C8A84B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mb-10 bg-white border border-red-200 rounded-2xl p-6 text-red-700">
        {error}
      </div>
    );
  }

  if (visibleReports.length === 0) {
    return null;
  }

  return (
    <div className="mb-12">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-2xl font-[Cormorant_Garamond] font-medium text-slate-900">
          {title}
        </h2>
      </div>

      <div className={viewMode === 'list' ? 'space-y-3' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'}>
        {visibleReports.map((report) => (
          <button
            key={report.id}
            type="button"
            onClick={() => navigate(`/subscription-reports/${report.id}`)}
            className={`group w-full min-w-0 text-left bg-white border border-slate-300 rounded-2xl p-6 shadow-sm hover:border-[#C8A84B]/80 hover:shadow-lg transition-all duration-300 ${viewMode === 'list' ? 'md:flex md:items-center md:gap-6' : ''}`}
          >
            <div className="mb-4 min-w-0 md:flex-1">
              <div className="mb-2 flex items-start justify-between gap-4">
                <h3 className="min-w-0 break-words [overflow-wrap:anywhere] text-xl font-[Cormorant_Garamond] font-semibold text-slate-950 group-hover:text-[#A77F16] transition-colors">
                  {report.title}
                </h3>
                <ReportIcon report={report} />
              </div>
              <p className="break-words text-sm font-medium text-slate-700">
                {report.category} | {(report.totalPages ?? report.total_pages)} pages
              </p>
              {formatFilterLabel(report, category).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {formatFilterLabel(report, category).map((tag) => (
                    <span key={tag} className="inline-flex max-w-full break-words [overflow-wrap:anywhere] items-center rounded-full border border-[#C8A84B]/40 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold text-amber-900">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              {report.description && (
                <p className="mt-4 break-words [overflow-wrap:anywhere] rounded-lg border-l-4 border-[#D4AF37] bg-amber-50/80 px-3 py-2 text-[15px] font-medium leading-relaxed text-slate-800 line-clamp-3 shadow-sm">
                  {report.description}
                </p>
              )}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-slate-100">
              <span className="text-xs font-medium text-slate-600">Uploaded Report</span>
              <span className="flex items-center gap-1 text-sm font-semibold text-[#A77F16] group-hover:text-[#8F6A10] transition-colors">
                View Report
                <ArrowUpRight className="w-4 h-4" />
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default SubscriptionReportSection;
